import React, { useEffect, useState } from "react";
import "./RunHistory.css";

/* Self-contained multi-run history: a left TOC of this session's pricing runs +
   a main area. Reloading a run is instant (cached output JSON) — it never
   re-calls the engine. Persists in sessionStorage so a refresh keeps the list.
   Kept dependency-free of the pages so it drops cleanly into the prod build. */

const val = (v) => (v && typeof v === "object" && "label" in v ? v.label : v);
const num = (v) => {
  const n = Number(String(val(v) == null ? "" : val(v)).replace(/[$,%\s]/g, ""));
  return isNaN(n) ? null : n;
};

export function buildRunLabel(data, product) {
  if (!data) return "Pricing run";
  const parts = [];
  const fico = num(data.borrower_credit_score_at_origination);
  if (fico) parts.push(`${fico} FICO`);
  const amt = num(data["2ndmortgage_amount_requested"] || data["2ndmortgage_amount"]);
  parts.push(amt ? `$${amt.toLocaleString("en-US")}` : "max amount");
  const purpose = val(data.loan_purpose);
  if (purpose) parts.push(String(purpose));
  const st = val(data.property_state);
  if (st) parts.push(String(st));
  return parts.join(" · ") || product || "Pricing run";
}

export function useRunHistory(storageKey = "bf-run-history") {
  const [runs, setRuns] = useState(() => {
    try {
      const v = JSON.parse(sessionStorage.getItem(storageKey));
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  });
  const [activeRunId, setActiveRunId] = useState(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(runs));
    } catch (e) {
      /* quota — non-fatal */
    }
  }, [runs, storageKey]);

  const addRun = (run) => {
    const id = run.id || `${run.filename || "run"}-${Date.now()}`;
    const entry = { id, createdAt: Date.now(), ...run };
    setRuns((prev) => [entry, ...prev.filter((r) => r.id !== id)].slice(0, 50));
    setActiveRunId(id);
    return id;
  };
  const selectRun = (id) => setActiveRunId(id);
  const newRun = () => setActiveRunId(null);
  const clearRuns = () => {
    setRuns([]);
    setActiveRunId(null);
  };

  // Merge the user's server-stored runs (metadata only; output loaded lazily).
  // Dedupe by input filename so this-session runs (with cached output) win.
  const seedServerRuns = (list) => {
    // Coerce to a real array: a non-array truthy resp.data would make .filter
    // throw inside this setRuns updater — a deferred render throw the page's
    // .catch cannot catch → blank page. (falsy-only `list || []` is not enough.)
    const arr = Array.isArray(list) ? list : [];
    setRuns((prev) => {
      // session runs key the filename as `filename`, server runs as `file`
      const seen = new Set(prev.map((r) => r.file || r.filename));
      const server = arr
        .filter((r) => r.file && !seen.has(r.file))
        .map((r) => ({
          id: "srv-" + r.id,
          serverId: r.id,
          file: r.file,
          loanType: r.loanType,
          isLC: r.loanType === "LC",
          product: r.loanType === "P" ? "Price" : r.loanType === "LC" ? "Quote" : "Rate",
          label: r.label || "Pricing run",
          createdAt: r.createdAt ? new Date(r.createdAt).getTime() : Date.now(),
          outputData: null,
          // Pre-snapshot runs whose files the nightly cleanup removed can never
          // reload — render them inert instead of click-to-error.
          loadable: r.loadable !== false,
        }));
      return [...prev, ...server]
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        .slice(0, 100);
    });
  };
  const setRunOutput = (id, outputData) => {
    setRuns((prev) => prev.map((r) => (r.id === id ? { ...r, outputData } : r)));
  };

  const activeRun = runs.find((r) => r.id === activeRunId) || null;

  return {
    runs,
    activeRunId,
    activeRun,
    addRun,
    selectRun,
    newRun,
    clearRuns,
    seedServerRuns,
    setRunOutput,
  };
}

const fmtTime = (ms) => {
  try {
    return new Date(ms).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
};

export function RunHistoryLayout({ runs, activeRunId, onSelect, onNew, children }) {
  const hasRuns = runs && runs.length > 0;
  if (!hasRuns) return <>{children}</>;
  return (
    <div className="bf-runlayout">
      <aside className="bf-runtoc">
        <div className="bf-runtoc-head">
          <span>Runs this session</span>
          <button type="button" className="bf-runtoc-new" onClick={onNew}>
            + New run
          </button>
        </div>
        <ul className="bf-runtoc-list">
          {runs.map((r) => {
            const dead = r.loadable === false && !r.outputData;
            return (
              <li key={r.id}>
                <button
                  type="button"
                  disabled={dead}
                  title={dead ? "This run predates saved snapshots and its files were removed by the nightly cleanup — it can't be reloaded." : undefined}
                  className={
                    "bf-runtoc-item" +
                    (r.id === activeRunId ? " active" : "") +
                    (dead ? " expired" : "")
                  }
                  onClick={dead ? undefined : () => onSelect(r.id)}
                >
                  <span className="bf-runtoc-label">{r.label}</span>
                  <span className="bf-runtoc-meta">
                    {dead ? (
                      <span className="bf-runtoc-badge expired">expired</span>
                    ) : r.product ? (
                      <span className="bf-runtoc-badge">{r.product}</span>
                    ) : null}
                    <span className="bf-runtoc-time">{fmtTime(r.createdAt)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
      <div className="bf-runmain">{children}</div>
    </div>
  );
}

export default RunHistoryLayout;
